const MAX_SOURCE_LENGTH = 64
const MAX_VALUE_LENGTH = 255
const PLACEHOLDERS = ['undefined', 'null']

const STRENGTH = { direct: 0, referrer: 1, utm: 2, click: 2 }

const SOURCE_ALIASES = {
  google: ['google'],
  gads: ['gads', 'google_ads', 'googleads', 'google-ads', 'adwords'],
  google_shopping: ['google_shopping', 'googleshopping'],
  bing: ['bing'],
  bing_ads: ['bing_ads', 'msads', 'microsoft_ads', 'microsoft'],
  meta: [
    'fb',
    'facebook',
    'ig',
    'instagram',
    'meta',
    'facebook-sitelink',
    'facebook_ads',
    'instagram_ads',
  ],
  tiktok: ['tiktok'],
  snapchat: ['snapchat'],
  pinterest: ['pinterest'],
  linkedin: ['linkedin'],
  twitter: ['twitter', 'x'],
  youtube: ['youtube'],
  reddit: ['reddit'],
  whatsapp: ['whatsapp'],
  telegram: ['telegram'],
  discord: ['discord'],
  newsletter: ['newsletter'],
  email: ['email', 'e-mail', 'mail', 'brevo', 'sendinblue', 'mailjet'],
  chatgpt: ['chatgpt', 'chatgpt.com', 'openai', 'chat.openai.com'],
  perplexity: ['perplexity', 'perplexity.ai'],
  gemini: ['gemini'],
  claude: ['claude', 'claude.ai'],
  copilot: ['copilot', 'copilot.com', 'copilot.microsoft.com'],
  mistral: ['mistral'],
  duckduckgo: ['duckduckgo'],
  yahoo: ['yahoo'],
  yandex: ['yandex'],
  ecosia: ['ecosia'],
  qwant: ['qwant'],
  brave: ['brave'],
  startpage: ['startpage'],
  baidu: ['baidu'],
  an: ['an'],
  direct: ['direct'],
  referral: ['referral'],
}

const FAMILY_BY_ALIAS = Object.entries(SOURCE_ALIASES).reduce(
  (map, [family, aliases]) => {
    aliases.forEach(alias => {
      map[alias] = family
    })
    return map
  },
  {}
)

export const CLICK_ID_SOURCES = [
  { key: 'gclid', source: 'gads', medium: 'paid' },
  { key: 'wbraid', source: 'gads', medium: 'paid' },
  { key: 'gbraid', source: 'gads', medium: 'paid' },
  { key: 'srsltid', source: 'google_shopping', medium: 'shopping' },
  { key: 'msclkid', source: 'bing_ads', medium: 'paid' },
  { key: 'fbclid', source: 'meta', medium: 'paid' },
  { key: 'ttclid', source: 'tiktok', medium: 'paid' },
  { key: 'sccid', source: 'snapchat', medium: 'paid' },
  { key: 'epik', source: 'pinterest', medium: 'paid' },
  { key: 'li_fat_id', source: 'linkedin', medium: 'paid' },
  { key: 'twclid', source: 'twitter', medium: 'paid' },
]

const TOP_LEVEL_CLICK_IDS = ['gclid', 'fbclid']

const GOOGLE_CLICK_IDS = ['gclid', 'wbraid', 'gbraid']
const GOOGLE_CLICK_ID_FORMAT = /^[A-Za-z0-9_-]{20,}$/

export const COOKIE_MAX_BYTES = 1536
export const IDENTIFIER_FIELDS = ['gclid', 'fbclid', 'click_ids']
const COOKIE_TEXT_LENGTH = 100
const COOKIE_TEXT_FIELDS = [
  'raw_source',
  'medium',
  'campaign',
  'content',
  'term',
  'referrer_host',
]
const COOKIE_SECONDARY_FIELDS = ['raw_source', 'content', 'term', 'click_ids']

export const PAYMENT_HOST_KEYWORDS = [
  'paypal',
  'systempay',
  'payzen',
  'stripe',
  'klarna',
  'scalapay',
  'getalma',
  'sogecommerce',
  'sogenactif',
  'cmcic',
  'creditmutuel',
  'scellius',
  'e-transactions',
  '3dsecure',
  'securepayments',
  'oney.fr',
  'oney.com',
  'apay.amazon',
  'payments.amazon',
  'payplug',
]

const TECHNICAL_HOST_PREFIXES = ['accounts.google.', 'tagassistant.google.com']

const EMAIL_HOST_KEYWORDS = [
  'sendibm',
  'mailjet',
  'mail.yahoo.',
  'webmail.',
  'messagerie.',
  'zimbra.',
  'mail.gmx.',
]

const EMAIL_HOSTS = [
  'mail.google.com',
  'com.google.android.gm',
  'mjt.lu',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.orange.fr',
  'mail.sfr.fr',
  'mail.free.fr',
  'mail.laposte.net',
  'mail.proton.me',
  'mail.icloud.com',
  'mail.com',
  'mail.aol.com',
]

const AI_SEGMENTS = {
  chatgpt: 'chatgpt',
  openai: 'chatgpt',
  perplexity: 'perplexity',
  gemini: 'gemini',
  claude: 'claude',
  copilot: 'copilot',
  mistral: 'mistral',
}

const SEARCH_ENGINE_DOMAINS = {
  google: 'google',
  bing: 'bing',
  duckduckgo: 'duckduckgo',
  yahoo: 'yahoo',
  yandex: 'yandex',
  ecosia: 'ecosia',
  qwant: 'qwant',
  baidu: 'baidu',
  brave: 'brave',
  startpage: 'startpage',
}

const SOCIAL_DOMAINS = {
  facebook: 'meta',
  fb: 'meta',
  instagram: 'meta',
  twitter: 'twitter',
  linkedin: 'linkedin',
  lnkd: 'linkedin',
  youtube: 'youtube',
  youtu: 'youtube',
  tiktok: 'tiktok',
  pinterest: 'pinterest',
  reddit: 'reddit',
  snapchat: 'snapchat',
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  discord: 'discord',
}

const SOCIAL_HOSTS = {
  'x.com': 'twitter',
  't.co': 'twitter',
  't.me': 'telegram',
}

const isUnder = (host, domain) => host === domain || host.endsWith(`.${domain}`)

const stripWww = host => host.replace(/^www\./, '')

const cap = (value, length) => value.slice(0, length)

export function isValidUtm(value) {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed !== '' && !PLACEHOLDERS.includes(trimmed.toLowerCase())
}

export function firstParam(query, key) {
  if (!query || typeof query !== 'object') return null
  const lower = key.toLowerCase()
  const actualKey =
    key in query ? key : Object.keys(query).find(k => k.toLowerCase() === lower)
  if (actualKey === undefined) return null
  const raw = query[actualKey]
  const value = Array.isArray(raw) ? raw[0] : raw
  return isValidUtm(value) ? cap(value.trim(), MAX_VALUE_LENGTH) : null
}

export function normalizeSource(raw) {
  if (!isValidUtm(raw)) return null
  const value = stripWww(raw.trim().toLowerCase()).replace(/\s+/g, '_')
  if (FAMILY_BY_ALIAS[value]) return FAMILY_BY_ALIAS[value]
  if (value.includes('.')) {
    const family = familyOfHost(value)
    if (family && family.source !== 'referral') return family.source
  }
  return cap(value, MAX_SOURCE_LENGTH)
}

export function isPaymentHost(host) {
  return PAYMENT_HOST_KEYWORDS.some(keyword => host.includes(keyword))
}

export function isTechnicalHost(host) {
  return TECHNICAL_HOST_PREFIXES.some(prefix => host.startsWith(prefix))
}

export function isEmailHost(host) {
  return (
    EMAIL_HOST_KEYWORDS.some(keyword => host.includes(keyword)) ||
    EMAIL_HOSTS.some(domain => isUnder(host, domain))
  )
}

function familyOfHost(host) {
  if (isPaymentHost(host) || isTechnicalHost(host)) return null
  if (isEmailHost(host)) return { source: 'email', medium: 'email' }

  const parts = host.split('.')
  const ai = parts.find(part => AI_SEGMENTS[part])
  if (ai) return { source: AI_SEGMENTS[ai], medium: 'ai' }

  const engine = parts.find(part => SEARCH_ENGINE_DOMAINS[part])
  if (engine)
    return { source: SEARCH_ENGINE_DOMAINS[engine], medium: 'organic' }

  const socialHost = Object.keys(SOCIAL_HOSTS).find(domain =>
    isUnder(host, domain)
  )
  if (socialHost) return { source: SOCIAL_HOSTS[socialHost], medium: 'social' }
  const social = parts.find(part => SOCIAL_DOMAINS[part])
  if (social) return { source: SOCIAL_DOMAINS[social], medium: 'social' }

  return { source: 'referral', medium: 'referral' }
}

function hostOf(referrer) {
  if (typeof referrer !== 'string' || referrer === '') return null
  try {
    return stripWww(new URL(referrer).hostname.toLowerCase()) || null
  } catch {
    return null
  }
}

function isSameSite(host, currentHost) {
  if (typeof currentHost !== 'string' || currentHost === '') return false
  return isUnder(host, stripWww(currentHost.toLowerCase()))
}

export function touchFromReferrer(referrer, currentHost) {
  const host = hostOf(referrer)
  if (!host || isSameSite(host, currentHost)) return null
  const family = familyOfHost(host)
  if (!family) return null
  return { ...family, kind: 'referrer', referrer_host: host }
}

const isWellFormedClickId = (key, value) =>
  !GOOGLE_CLICK_IDS.includes(key) || GOOGLE_CLICK_ID_FORMAT.test(value)

function clickIdsOf(query) {
  const found = {}
  CLICK_ID_SOURCES.forEach(({ key }) => {
    const value = firstParam(query, key)
    if (value === null || !isWellFormedClickId(key, value)) return
    if (TOP_LEVEL_CLICK_IDS.includes(key)) {
      found[key] = value
      return
    }
    found.click_ids = { ...(found.click_ids || {}), [key]: value }
  })
  return found
}

function utmFieldsOf(query) {
  const fields = {}
  const medium = firstParam(query, 'utm_medium')
  const campaign = firstParam(query, 'utm_campaign')
  const content = firstParam(query, 'utm_content')
  const term = firstParam(query, 'utm_term')
  if (medium) fields.medium = medium
  if (campaign) fields.campaign = campaign
  if (content) fields.content = content
  if (term) fields.term = term
  return fields
}

export function touchFromQuery(query) {
  const clickIds = clickIdsOf(query)
  const fields = utmFieldsOf(query)
  const source = firstParam(query, 'utm_source')

  if (source) {
    const family = normalizeSource(source)
    return {
      source: family,
      ...(family !== source ? { raw_source: source } : {}),
      ...fields,
      kind: 'utm',
      ...clickIds,
    }
  }

  const click = CLICK_ID_SOURCES.find(
    ({ key }) => clickIds[key] !== undefined || clickIds.click_ids?.[key]
  )
  if (!click) return null

  return {
    source: click.source,
    medium: click.medium,
    ...fields,
    kind: 'click',
    ...clickIds,
  }
}

export function directTouch() {
  return { source: 'direct', kind: 'direct' }
}

export function strengthOf(kind) {
  return Object.prototype.hasOwnProperty.call(STRENGTH, kind)
    ? STRENGTH[kind]
    : -1
}

const field = (touch, name) => (touch && touch[name]) || null

export function sameTouch(a, b) {
  if (!a || !b) return !a && !b
  return ['source', 'medium', 'campaign', 'referrer_host'].every(
    name => field(a, name) === field(b, name)
  )
}

const clickIdValues = touch =>
  CLICK_ID_SOURCES.map(({ key }) =>
    TOP_LEVEL_CLICK_IDS.includes(key)
      ? field(touch, key)
      : field(touch?.click_ids, key)
  )

const sameClickIds = (a, b) => {
  const right = clickIdValues(b)
  return clickIdValues(a).every((value, index) => value === right[index])
}

const identicalTouch = (a, b) => sameTouch(a, b) && sameClickIds(a, b)

const beats = (touch, current) =>
  strengthOf(touch.kind) >= strengthOf(current?.kind) &&
  !sameTouch(touch, current)

export function mergeTouch(state, touch) {
  if (!touch) return state || null
  const stamped = touch.captured_at
    ? touch
    : { ...touch, captured_at: new Date().toISOString() }
  const first = state?.first || stamped
  const current = state?.last
  const newer =
    !current?.captured_at ||
    !stamped.captured_at ||
    stamped.captured_at >= current.captured_at
  const last =
    !current ||
    (newer &&
      strengthOf(stamped.kind) >= strengthOf(current.kind) &&
      !identicalTouch(stamped, current))
      ? stamped
      : current
  return { first, last }
}
export function hasIdentifiers(touch) {
  return Boolean(
    touch &&
      (touch.gclid || touch.fbclid || Object.keys(touch.click_ids || {}).length)
  )
}
export function withoutIdentifiers(touch) {
  if (!touch) return touch
  const bare = { ...touch }
  IDENTIFIER_FIELDS.forEach(name => delete bare[name])
  return bare
}
const identifiersOf = touch =>
  Object.fromEntries(
    IDENTIFIER_FIELDS.filter(name => touch?.[name] !== undefined).map(name => [
      name,
      touch[name],
    ])
  )
export function adoptIdentifiers(state, pending) {
  if (!pending?.first) return state
  if (!state?.first) return pending
  const donors = [pending.first, pending.last || pending.first]
  const adopt = touch => {
    const donor = donors.find(candidate => sameTouch(candidate, touch))
    return donor ? { ...touch, ...identifiersOf(donor) } : touch
  }
  return { first: adopt(state.first), last: adopt(state.last || state.first) }
}

export function isDirectOnly(state) {
  if (!state?.first) return true
  return [state.first, state.last || state.first].every(
    touch => touch.kind === 'direct'
  )
}

function extraOf(order) {
  const extra = order?.utm_extra
  if (!extra) return {}
  if (typeof extra !== 'string') return extra
  try {
    return JSON.parse(extra) || {}
  } catch {
    return {}
  }
}

export function currentTouchOfOrder(order) {
  const extra = extraOf(order)
  const source = field(order, 'utm_source')
  return {
    source,
    medium: field(order, 'utm_medium'),
    campaign: field(order, 'utm_campaign'),
    referrer_host: extra.referrer_host || null,
    kind: extra.kind || (source ? 'utm' : null),
  }
}

function bringsNewClickId(touch, order) {
  const extra = extraOf(order)
  if (touch.gclid && touch.gclid !== order.gclid) return true
  if (touch.fbclid && touch.fbclid !== extra.fbclid) return true
  const known = extra.click_ids || {}
  return Object.entries(touch.click_ids || {}).some(
    ([key, value]) => value !== known[key]
  )
}

export function touchesToSend(state, order) {
  if (!state?.first || !order || order.ordered_at) return []
  const last = state.last || state.first
  const touches = identicalTouch(state.first, last)
    ? [state.first]
    : [state.first, last]
  const current = currentTouchOfOrder(order)
  const accepted = touches.some(
    touch =>
      !order.first_utm_source ||
      beats(touch, current) ||
      bringsNewClickId(touch, order)
  )
  return accepted ? touches : []
}

function touchFromLegacyCookie(cookie) {
  const clickIds = clickIdsOf(cookie)
  const source = isValidUtm(cookie.utm_source)
    ? normalizeSource(cookie.utm_source)
    : null
  const click = CLICK_ID_SOURCES.find(
    ({ key }) => clickIds[key] !== undefined || clickIds.click_ids?.[key]
  )
  if (!source && !click) return null

  const touch = { source: source || click.source }
  const medium = isValidUtm(cookie.utm_medium)
    ? cookie.utm_medium
    : click?.medium
  if (medium) touch.medium = medium
  if (isValidUtm(cookie.utm_campaign)) touch.campaign = cookie.utm_campaign
  if (isValidUtm(cookie.utm_content)) touch.content = cookie.utm_content
  if (isValidUtm(cookie.utm_term)) touch.term = cookie.utm_term
  if (isValidUtm(cookie.referrer_host)) {
    touch.referrer_host = cookie.referrer_host
    touch.kind = 'referrer'
  } else {
    touch.kind = click ? 'click' : 'utm'
  }
  return { ...touch, ...clickIds }
}

export function stateFromCookie(value) {
  let cookie = value
  if (typeof cookie === 'string') {
    try {
      cookie = JSON.parse(cookie)
    } catch {
      return null
    }
  }
  if (!cookie || typeof cookie !== 'object') return null
  if (cookie.first && typeof cookie.first === 'object') {
    return { first: cookie.first, last: cookie.last || cookie.first }
  }
  const touch = touchFromLegacyCookie(cookie)
  return touch ? mergeTouch(null, touch) : null
}

const cookieBytes = state => encodeURIComponent(JSON.stringify(state)).length

const compactTouch = touch => {
  const compact = { ...touch }
  COOKIE_TEXT_FIELDS.forEach(name => {
    if (typeof compact[name] === 'string') {
      compact[name] = cap(compact[name], COOKIE_TEXT_LENGTH)
    }
  })
  return compact
}

const withoutSecondaryFields = touch =>
  Object.fromEntries(
    Object.entries(touch).filter(
      ([name]) => !COOKIE_SECONDARY_FIELDS.includes(name)
    )
  )

export function stateForCookie(state) {
  if (!state?.first) return null
  const lastTouch = state.last || state.first
  const single = identicalTouch(state.first, lastTouch)
  const compact = single
    ? { first: compactTouch(state.first) }
    : { first: compactTouch(state.first), last: compactTouch(lastTouch) }
  if (cookieBytes(compact) <= COOKIE_MAX_BYTES) return compact
  const trimmed = single
    ? { first: withoutSecondaryFields(compact.first) }
    : {
        first: withoutSecondaryFields(compact.first),
        last: withoutSecondaryFields(compact.last),
      }
  return cookieBytes(trimmed) <= COOKIE_MAX_BYTES ? trimmed : null
}
